import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flowIdeas, insights } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { Select, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useMediaQuery } from '@repletoryreach/sdk-utils/hooks';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useI18n } from '../../../i18n/I18nProvider';
import { formatCredits } from '../../../lib/credits';
import { formatCount, formatDateTime } from '../../../lib/format';
import { formatLocalDate } from '../../bookings/lib/time';
import { AiPanel, type Composer } from './AiPanel';
import { BuildBar, BuildConfirm, BuildResults } from './Build';
import { SettingsDrawer } from './SettingsDrawer';
import { SuggestionCard } from './SuggestionCard';
import { LooseEndsList } from '../components/LooseEndsList';
import { LOOSE_ENDS_KEY } from '../components/loose-ends';
import {
  IDEAS_KEY, PRIORITIES, buildRequest, filterItems, gapsText, hasOpenQuestions, ideasErrorText, isBusy, modelOf, nextPicks, offered,
  setLanguage, settingsSummary
} from './lib/data';
import './flow-ideas.css';

const MUTED = 'var(--rr-color-text-muted)';
const NARROW = '(max-width: 1100px)'; // i18n-ignore: a media query, not words

/**
 * Suggest flows with AI (owner, 30 October 2026: "AI could suggest and create their flows ... user can pick so that it
 * can be created and user does not pick then it won't be created"). A page of its own: the person starts in the centre
 * with their ideas and settings, the AI works and talks on the right (what it is doing, its questions, a back-and-forth
 * chat), and the suggestions it writes land in the centre to pick from. Only the picked ones are built, in the
 * background, switched off. Owners and admins only (the Flows prefix is theirs).
 */
export default function FlowIdeasPage() {
  const { id } = useParams();
  return id ? <SetView key={id} id={id} /> : <StartView />;
}

/** The centre and the AI's side; two tabs below 1 100 px, with a dot on AI while it works or has asked something. */
function Layout({ centre, ai, attention }: { centre: ReactNode; ai: ReactNode; attention: boolean }) {
  // The page's title stays above the tabs on a narrow screen, so it shows on both.
  const { t } = useI18n();
  const narrow = useMediaQuery(NARROW);
  const [tab, setTab] = useState<'flows' | 'ai'>('flows');
  const asideRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (narrow) return undefined;
    // The AI's side fills the screen from where its top sits to the bottom, as the Flows workspace's sidebar.
    const fit = () => {
      const el = asideRef.current;
      if (!el) return;
      const top = Math.max(12, el.getBoundingClientRect().top);
      // 84px below it: the console's floating assistant button sits in that corner, clear of Send.
      el.style.height = `calc(100vh - ${Math.round(top) + 84}px)`;
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('scroll', fit, { passive: true, capture: true });
    return () => { window.removeEventListener('resize', fit); window.removeEventListener('scroll', fit, { capture: true }); };
  }, [narrow]);
  // A request to show the AI (the page's "Ask the AI to change this") switches the tab on a narrow screen.
  useEffect(() => {
    const show = () => setTab('ai');
    window.addEventListener('rr-ideas-show-ai', show);
    return () => window.removeEventListener('rr-ideas-show-ai', show);
  }, []);

  if (narrow) {
    return (
      <div className="rr-ideas is-narrow">
        <Header />
        <Tabs value={tab} onChange={(v) => setTab(v === 'ai' ? 'ai' : 'flows')}>
          <Tabs.List>
            <Tabs.Tab value="flows" data-testid="ideas-tab-flows">{t('flowIdeas.tabs.flows')}</Tabs.Tab>
            <Tabs.Tab value="ai" data-testid="ideas-tab-ai">
              {t('flowIdeas.tabs.ai')}
              {attention && <span className="rr-ideas__tab-dot" aria-label={t('flowIdeas.tabs.attention')} data-testid="ideas-tab-dot" />}
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
        {tab === 'flows' ? <main className="rr-ideas__centre">{centre}</main> : <aside className="rr-ideas__ai" aria-label={t('flowIdeas.ai.title')}>{ai}</aside>}
      </div>
    );
  }
  return (
    <div className="rr-ideas">
      <main className="rr-ideas__centre"><Header />{centre}</main>
      <aside ref={asideRef} className="rr-ideas__ai" aria-label={t('flowIdeas.ai.title')} data-testid="ideas-ai" data-guide="ideas-ai">{ai}</aside>
    </div>
  );
}

function Header() {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <PageHeader title={t('flowIdeas.title')} description={t('flowIdeas.description')} withDivider={false}
      actions={(
        <Group gap="sm">
          <HelpButton guide="automation" />
          <Button variant="default" size="sm" onClick={() => nav('/flows')}>{t('flowIdeas.backToFlows')}</Button>
        </Group>
      )} />
  );
}

/** "28 Oct": the Insights report's date as the settings name it. */
function useInsightsDate(options?: flowIdeas.FlowIdeaOptions): string | undefined {
  const { locale } = useI18n();
  const d = options?.insights?.finished_at ?? options?.insights?.to;
  return d ? formatLocalDate(d.slice(0, 10), locale.includes('-') ? locale : `${locale}-ZA`, { day: 'numeric', month: 'short', year: 'numeric' }) : undefined;
}

/** What a message would cost, asked again a moment after the person stops typing. */
function useTurnEstimate(model: string | undefined, chars: number, setId?: string, enabled = true): flowIdeas.FlowIdeaEstimate | null {
  const [estimate, setEstimate] = useState<flowIdeas.FlowIdeaEstimate | null>(null);
  useEffect(() => {
    if (!model || !enabled) return undefined;
    let live = true;
    const timer = window.setTimeout(() => {
      flowIdeas.estimateFlowIdeaTurn({ set_id: setId, model, chars })
        .then((r) => { if (live) setEstimate(r.data); })
        .catch(() => { if (live) setEstimate(null); });
    }, 600);
    return () => { live = false; window.clearTimeout(timer); };
  }, [model, chars, setId, enabled]);
  return estimate;
}

function useOptions() {
  return useQuery({ queryKey: [IDEAS_KEY, 'options'], queryFn: () => flowIdeas.getFlowIdeaOptions(), select: (r) => r.data, staleTime: 60_000 });
}

// ── A new set ───────────────────────────────────────────────────────────────

function StartView() {
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const options = useOptions();
  const [settings, setSettings] = useState<flowIdeas.FlowIdeaSettings | null>(null);
  const [text, setText] = useState('');
  const [gaps, setGaps] = useState<string | undefined>(undefined);
  const [drawer, setDrawer] = useState(false);
  const insightsDate = useInsightsDate(options.data);
  useEffect(() => { if (!settings && options.data) setSettings(options.data.defaults); }, [options.data, settings]);

  // "Suggest flows for these" on an Insights report's Gaps tab: its gaps become the ideas, and the report is used.
  const reportId = params.get('from') === 'insights' ? params.get('report') : null;
  const report = useQuery({ queryKey: ['insights', 'report', reportId], queryFn: () => insights.getInsightReport(reportId!), enabled: !!reportId, select: (r) => r.data });
  useEffect(() => {
    if (!report.data || gaps !== undefined) return;
    const lines = gapsText(t, report.data);
    setGaps(lines);
    setText((cur) => cur || [t('flowIdeas.fromInsights.intro'), lines].filter(Boolean).join('\n'));
    setSettings((cur) => (cur ? { ...cur, use_insights: true } : cur));
  }, [report.data, gaps, t]);

  const estimate = useTurnEstimate(settings?.suggest_model, text.length, undefined, !!settings);
  const model = modelOf(options.data, settings?.suggest_model ?? '');
  const price = estimate?.minor ?? model?.turn_minor ?? null;

  const create = useMutation({
    mutationFn: () => flowIdeas.createFlowIdeaSet({ settings: settings!, text: text.trim(), language: setLanguage(locale), insight_gaps: gaps || undefined }),
    onSuccess: (res) => {
      qc.setQueryData([IDEAS_KEY, 'set', res.data.id], res);
      void qc.invalidateQueries({ queryKey: [IDEAS_KEY, 'sets'] });
      nav(`/flows/ideas/${res.data.id}`);
    }
  });

  const centre = (
    <>
      {options.error && <Alert color="error" role="alert">{ideasErrorText(t, options.error)}</Alert>}
      {!settings ? <Skeleton height={260} /> : (
        <Card withBorder data-guide="ideas-start" data-testid="ideas-start">
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={4}>{t('flowIdeas.start.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('flowIdeas.start.intro')}</Text>
            </Stack>
            <Textarea label={t('flowIdeas.start.ideas')} description={t('flowIdeas.start.ideasHint')} placeholder={t('flowIdeas.start.ideasPlaceholder')}
              value={text} minRows={6} maxRows={16} autosize onChange={(e) => setText(e.currentTarget.value)} data-testid="ideas-text" />
            <Stack gap={6}>
              <Group justify="space-between" gap="xs" wrap="wrap">
                <Text role="label">{t('flowIdeas.start.settings')}</Text>
                <Button size="xs" variant="default" onClick={() => setDrawer(true)} data-testid="ideas-open-settings">{t('flowIdeas.start.change')}</Button>
              </Group>
              <div className="rr-ideas__summary" data-testid="ideas-summary">
                {settingsSummary(t, settings, options.data, insightsDate).map((p) => <span key={p} className="rr-ideas__summary-part">{p}</span>)}
              </div>
            </Stack>
            {create.error && <Alert color="error" role="alert">{ideasErrorText(t, create.error)}</Alert>}
            {estimate && !estimate.affordable && <Alert color="warning">{t('flowIdeas.error.credits')}</Alert>}
            <Group justify="space-between" align="center" gap="sm" wrap="wrap">
              <Text role="caption" c={MUTED} data-testid="ideas-start-price">
                {price == null ? t('flowIdeas.start.priceUnknown') : t('flowIdeas.start.price', { credits: formatCredits(price, t) })}
              </Text>
              <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!settings} data-testid="ideas-suggest">
                {text.trim() ? t('flowIdeas.start.suggest') : t('flowIdeas.start.suggestFromKb')}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}
      <History />
      {settings && (
        <SettingsDrawer opened={drawer} onClose={() => setDrawer(false)} settings={settings} onChange={setSettings}
          options={options.data} insightsDate={insightsDate} />
      )}
    </>
  );
  return <Layout centre={centre} attention={false} ai={<AiPanel set={null} composer={IDLE} onSend={() => undefined} onFresh={() => undefined} freshing={false} />} />;
}

const IDLE: Composer = { draft: '', setDraft: () => undefined, estimate: null, sending: false, error: null };

/** Earlier sets, kept 90 days, so suggestions not built yet can be picked later. */
function History() {
  const { t } = useI18n();
  const list = useQuery({ queryKey: [IDEAS_KEY, 'sets'], queryFn: () => flowIdeas.listFlowIdeaSets(20), select: (r) => r.data });
  const sets = list.data ?? [];
  if (list.isPending || sets.length === 0) return null;
  return (
    <Card withBorder data-testid="ideas-history">
      <Stack gap="sm">
        <Title order={5}>{t('flowIdeas.history.title')}</Title>
        <Stack gap={6} className="rr-ideas__history">
          {sets.map((s) => (
            <Link key={s.id} to={`/flows/ideas/${s.id}`} data-testid="ideas-history-row">
              <Group justify="space-between" gap="sm" wrap="wrap">
                <Text role="bodySmall" fw={600} className="rr-ideas__history-title">{s.title ?? t('flowIdeas.history.fromKb')}</Text>
                <Text role="caption" c={MUTED}>
                  {t('flowIdeas.history.counts', { suggestions: formatCount(s.suggestions), created: formatCount(s.created) })} · {formatDateTime(s.updated_at)}
                </Text>
              </Group>
            </Link>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

// ── A set under way ─────────────────────────────────────────────────────────

function SetView({ id }: { id: string }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const qc = useQueryClient();
  const options = useOptions();
  const insightsDate = useInsightsDate(options.data);
  const key = [IDEAS_KEY, 'set', id];
  const query = useQuery({
    queryKey: key,
    queryFn: () => flowIdeas.getFlowIdeaSet(id),
    refetchInterval: (q) => (isBusy(q.state.data?.data) ? 2000 : false)
  });
  const set = query.data?.data ?? null;
  const put = (res: { data: flowIdeas.FlowIdeaSet }) => qc.setQueryData(key, res);

  // Picks: a new grounded suggestion is ticked, one already seen keeps the person's choice.
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const seen = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!set) return;
    const next = nextPicks(set.items, picked, seen.current);
    seen.current = next.seen;
    if (next.picked.size !== picked.size || [...next.picked].some((n) => !picked.has(n))) setPicked(next.picked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set?.items]);

  const [goal, setGoal] = useState('');
  const [priority, setPriority] = useState('');
  const [buildModel, setBuildModel] = useState<string | null>(null);
  const model = buildModel ?? set?.settings.build_model ?? '';
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState('');
  const focusComposer = useRef<() => void>(() => undefined);
  const setFocus = useCallback((f: () => void) => { focusComposer.current = f; }, []);
  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState<flowIdeas.FlowIdeaSettings | null>(null);
  // A replace (one, or `all`) or a restore under way (2 November 2026).
  const [acting, setActing] = useState<number | 'all' | null>(null);

  const estimate = useTurnEstimate(set?.settings.suggest_model, draft.length, id, !!set && draft.trim().length > 0);

  const send = useMutation({
    mutationFn: (text: string) => flowIdeas.sendFlowIdeaMessage(id, text),
    onSuccess: (res) => { put(res); setDraft(''); }
  });
  const build = useMutation({
    mutationFn: () => flowIdeas.buildFlowIdeas(id, { items: [...picked].sort((a, b) => a - b).map((n) => ({ n })), model }),
    onSuccess: (res) => { put(res); setConfirming(false); notifySuccess(t('flowIdeas.build.started'), t('flowIdeas.title')); }
  });
  const cancel = useMutation({
    mutationFn: () => flowIdeas.cancelFlowIdeaSet(id),
    onSuccess: put,
    onError: (e) => notifyError(ideasErrorText(t, e), t('flowIdeas.title'))
  });
  const fresh = useMutation({
    mutationFn: () => flowIdeas.startFreshFlowIdeaSet(id),
    onSuccess: (res) => { qc.setQueryData([IDEAS_KEY, 'set', res.data.id], res); nav(`/flows/ideas/${res.data.id}`); },
    onError: (e) => notifyError(ideasErrorText(t, e), t('flowIdeas.title'))
  });
  const saveSettings = useMutation({
    mutationFn: (s: flowIdeas.FlowIdeaSettings) => flowIdeas.saveFlowIdeaSettings(id, s),
    onSuccess: (res) => { put(res); setDrawer(false); notifySuccess(t('flowIdeas.settings.saved'), t('flowIdeas.title')); },
    onError: (e) => notifyError(ideasErrorText(t, e), t('flowIdeas.title'))
  });
  /*
   * Replace an old version with its improvement, all of them, or put one back (2 November 2026). Each changes flows
   * (links, names, which one is on), so the flows, the loose ends under the results and the set are asked again.
   */
  const changed = () => {
    void qc.invalidateQueries({ queryKey: ['flows'] });
    void qc.invalidateQueries({ queryKey: ['flow'] });
    void qc.invalidateQueries({ queryKey: key });
  };
  const replaceOne = useMutation({
    mutationFn: (n: number) => { setActing(n); return flowIdeas.replaceWithImprovedFlow(id, n); },
    onSuccess: (res) => { put(res); changed(); notifySuccess(t('flowIdeas.replace.doneOne'), t('flowIdeas.title')); },
    onError: (e) => notifyError(ideasErrorText(t, e), t('flowIdeas.title')),
    onSettled: () => setActing(null)
  });
  const replaceAll = useMutation({
    mutationFn: () => { setActing('all'); return flowIdeas.replaceAllWithImprovedFlows(id); },
    onSuccess: (res) => {
      const before = new Set((set?.items ?? []).filter((i) => i.replaced_at).map((i) => i.n));
      const count = res.data.items.filter((i) => i.replaced_at && !before.has(i.n)).length;
      put(res);
      changed();
      notifySuccess(t('flowIdeas.replace.doneAll', { count: formatCount(count) }), t('flowIdeas.title'));
    },
    onError: (e) => notifyError(ideasErrorText(t, e), t('flowIdeas.title')),
    onSettled: () => setActing(null)
  });
  const restore = useMutation({
    mutationFn: (n: number) => { setActing(n); return flowIdeas.restoreOldFlowVersion(id, n); },
    onSuccess: (res) => { put(res); changed(); notifySuccess(t('flowIdeas.restore.done'), t('flowIdeas.title')); },
    onError: (e) => notifyError(ideasErrorText(t, e), t('flowIdeas.title')),
    onSettled: () => setActing(null)
  });
  // A build that has just finished changed the business's flows: the loose ends under the results are checked again.
  const wasBuilding = useRef(false);
  useEffect(() => {
    if (!set) return;
    if (wasBuilding.current && set.status !== 'building') void qc.invalidateQueries({ queryKey: LOOSE_ENDS_KEY });
    wasBuilding.current = set.status === 'building';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set?.status]);

  // "Build 1, 2 and 5" in the chat: the AI proposes it; the cards are picked here and the person confirms.
  const handled = useRef(0);
  useEffect(() => {
    if (!set) return;
    if (handled.current === 0) handled.current = Math.max(0, ...set.messages.map((m) => m.n));
    const req = buildRequest(set.messages, handled.current);
    if (!req) return;
    handled.current = req.n;
    const open = new Set(offered(set.items).map((i) => i.n));
    const wanted = req.build.filter((n) => open.has(n));
    if (wanted.length > 0) { setPicked(new Set(wanted)); setConfirming(true); }
  }, [set]);

  const items = set ? offered(set.items) : [];
  const shown = filterItems(items, goal, priority);
  const busy = isBusy(set);
  const attention = !!set && (set.status === 'thinking' || hasOpenQuestions(set.messages));
  const buildFallback = modelOf(options.data, model)?.build_minor ?? null;

  const ask = (name: string) => {
    setDraft(t('flowIdeas.card.askPrefill', { name }));
    window.dispatchEvent(new Event('rr-ideas-show-ai'));
    window.setTimeout(() => focusComposer.current(), 50);
  };

  const composer: Composer = {
    draft, setDraft, estimate, sending: send.isPending,
    error: send.error ? ideasErrorText(t, send.error) : null
  };

  if (query.error && !set) {
    return <Layout attention={false} centre={<Alert color="error" role="alert">{ideasErrorText(t, query.error)}</Alert>} ai={null} />;
  }

  const centre = (
    <>
      {!set ? <Skeleton height={200} /> : (
        <>
          <Card withBorder data-testid="ideas-set-head">
            <Stack gap="sm">
              <Group justify="space-between" gap="sm" wrap="wrap">
                <Stack gap={2} style={{ minWidth: 0, flex: '1 1 260px' }}>
                  <Title order={4}>{t('flowIdeas.set.title')}</Title>
                  <Text role="caption" c={MUTED}>{t(`flowIdeas.status.${set.status}`)} · {formatDateTime(set.created_at)}</Text>
                </Stack>
                <Group gap="xs">
                  {busy && <Button size="xs" variant="default" onClick={() => cancel.mutate()} loading={cancel.isPending} data-testid="ideas-cancel">{t('flowIdeas.set.cancel')}</Button>}
                  <Button size="xs" variant="default" disabled={busy} onClick={() => { setEditing(set.settings); setDrawer(true); }} data-testid="ideas-open-settings">{t('flowIdeas.start.change')}</Button>
                  <Button size="xs" variant="subtle" onClick={() => nav('/flows/ideas')} data-testid="ideas-new-set">{t('flowIdeas.set.new')}</Button>
                </Group>
              </Group>
              <div className="rr-ideas__summary" data-testid="ideas-summary">
                {settingsSummary(t, set.settings, options.data, insightsDate).map((p) => <span key={p} className="rr-ideas__summary-part">{p}</span>)}
              </div>
              {set.error && <Alert color="error" role="alert">{set.error}</Alert>}
            </Stack>
          </Card>

          <BuildResults set={set} acting={acting} onReplace={(n) => replaceOne.mutate(n)} onReplaceAll={() => replaceAll.mutate()}
            onRestore={(n) => restore.mutate(n)} />

          {/* After a build or a replace: what still leads nowhere in the business's switched-on flows (2 November 2026). */}
          {set.status !== 'building' && set.items.some((i) => i.status === 'created') && (
            <section className="rr-ideas__card" data-testid="ideas-loose-ends">
              <LooseEndsList onChanged={() => void qc.invalidateQueries({ queryKey: key })} />
            </section>
          )}

          {items.length === 0 ? (
            set.status === 'thinking' ? (
              <Card withBorder data-testid="ideas-waiting">
                <Stack gap="sm">
                  <Text role="bodySmall">{t('flowIdeas.set.waiting')}</Text>
                  <div className="rr-ideas__cards"><Skeleton height={220} /><Skeleton height={220} /></div>
                </Stack>
              </Card>
            ) : sentToBuildCount(set) === 0 ? (
              <EmptyState title={t('flowIdeas.set.none')} description={t('flowIdeas.set.noneHint')} />
            ) : null
          ) : (
            <Stack gap="sm" data-guide="ideas-cards">
              <Group justify="space-between" align="flex-end" gap="sm" wrap="wrap">
                <Stack gap={2}>
                  <Title order={4}>{t('flowIdeas.cards.title', { count: items.length })}</Title>
                  <Text role="caption" c={MUTED}>{t('flowIdeas.cards.hint')}</Text>
                </Stack>
                <Group gap="xs" wrap="wrap">
                  <Select aria-label={t('flowIdeas.cards.goal')} value={goal} onChange={(e) => setGoal(e.currentTarget.value)} data-testid="ideas-filter-goal"
                    data={[{ value: '', label: t('flowIdeas.cards.anyGoal') }, ...flowIdeas.FLOW_IDEA_GOALS.map((g) => ({ value: g, label: t(`flowIdeas.goal.${g}`) }))]} />
                  <Select aria-label={t('flowIdeas.cards.priority')} value={priority} onChange={(e) => setPriority(e.currentTarget.value)} data-testid="ideas-filter-priority"
                    data={[{ value: '', label: t('flowIdeas.cards.anyPriority') }, ...PRIORITIES.map((p) => ({ value: p, label: t(`flowIdeas.priority.${p}`) }))]} />
                </Group>
              </Group>
              {shown.length === 0 ? <Text role="bodySmall" c={MUTED}>{t('flowIdeas.cards.filteredOut')}</Text> : (
                <div className="rr-ideas__cards">
                  {shown.map((item) => (
                    <SuggestionCard key={item.n} item={item} picked={picked.has(item.n)} fallbackMinor={buildFallback}
                      onPick={(on) => setPicked((cur) => { const next = new Set(cur); if (on) next.add(item.n); else next.delete(item.n); return next; })}
                      onAsk={() => ask(item.proposal.name)} />
                  ))}
                </div>
              )}
              <BuildBar items={items} picked={picked} options={options.data} model={model} onModel={setBuildModel}
                onBuild={() => { build.reset(); setConfirming(true); }} busy={busy} />
            </Stack>
          )}
        </>
      )}
      {set && editing && (
        <SettingsDrawer opened={drawer} onClose={() => setDrawer(false)} settings={editing} onChange={setEditing}
          options={options.data} insightsDate={insightsDate} onSave={() => saveSettings.mutate(editing)} saving={saveSettings.isPending} />
      )}
      {set && (
        <BuildConfirm opened={confirming} onClose={() => setConfirming(false)} items={items} picked={picked} options={options.data} model={model}
          onConfirm={() => build.mutate()} loading={build.isPending} error={build.error ? ideasErrorText(t, build.error) : null} />
      )}
    </>
  );

  return (
    <Layout centre={centre} attention={attention}
      ai={<AiPanel set={set} composer={composer} onSend={(text) => send.mutate(text)} onFresh={() => fresh.mutate()} freshing={fresh.isPending} onFocusRef={setFocus} />} />
  );
}

const sentToBuildCount = (set: flowIdeas.FlowIdeaSet) =>
  set.items.filter((i) => i.status !== 'proposed' && i.status !== 'removed').length;
